/* Athlete Kingdom — storefront behaviour: cart drawer (Shopify AJAX cart), variant picker, motion. */
(() => {
  const AK = window.AK || {};
  const routes = AK.routes || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  /* ---------- Money, following the shop's own money format ---------- */

  function money(cents) {
    const format = AK.moneyFormat || '{{amount}}';
    const value = Number(cents) / 100;
    const fmt = (decimals, thousands, decimal) => {
      const [whole, frac] = value.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    const styles = {
      amount: () => fmt(2, ',', '.'),
      amount_no_decimals: () => fmt(0, ',', '.'),
      amount_with_comma_separator: () => fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(0, '.', ','),
      amount_with_space_separator: () => fmt(2, ' ', ','),
      amount_with_apostrophe_separator: () => fmt(2, "'", '.'),
    };
    const text = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (styles[key] || styles.amount)());
    const el = document.createElement('textarea');
    el.innerHTML = text; // decode entities such as &#82; in money formats
    return el.value;
  }

  function h(tag, attrs = {}, ...children) {
    const el = document.createElement(tag);
    for (const [key, val] of Object.entries(attrs)) {
      if (val == null || val === false) continue;
      if (key === 'class') el.className = val;
      else if (key.startsWith('on')) el.addEventListener(key.slice(2), val);
      else el.setAttribute(key, val === true ? '' : val);
    }
    for (const child of children.flat()) if (child != null) el.append(child);
    return el;
  }

  /* ---------- Cart API ---------- */

  const cartApi = {
    async get() {
      const res = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return res.json();
    },
    async add(formData) {
      const res = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag.');
      return data;
    },
    async change(key, quantity) {
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not update bag.');
      return data;
    },
  };

  /* ---------- Cart drawer ---------- */

  const drawer = $('[data-ak-drawer]');
  let lastFocus = null;

  function setCount(count, bump) {
    $$('[data-ak-cart-count]').forEach((el) => {
      el.textContent = count;
      if (bump && el.classList.contains('ak-header__count')) {
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      }
    });
  }

  function renderCart(cart) {
    if (!drawer) return;
    const list = $('[data-ak-cart-items]', drawer);
    const empty = $('[data-ak-cart-empty]', drawer);
    const foot = $('[data-ak-cart-foot]', drawer);
    setCount(cart.item_count);
    list.replaceChildren();
    const hasItems = cart.item_count > 0;
    list.hidden = !hasItems;
    empty.hidden = hasItems;
    foot.hidden = !hasItems;
    if (!hasItems) return;

    cart.items.forEach((item, i) => {
      const img = item.image
        ? h('img', { src: `${item.image}${item.image.includes('?') ? '&' : '?'}width=240`, alt: '', loading: 'lazy' })
        : null;
      const variant = item.variant_title && !item.product_has_only_default_variant ? item.variant_title : '';
      const row = h('div', { class: 'ak-line', style: `--i:${i}` },
        h('a', { class: 'ak-line__img', href: item.url, tabindex: '-1' }, img),
        h('div', {},
          h('p', { class: 'ak-line__title' }, h('a', { href: item.url }, item.product_title)),
          h('p', { class: 'ak-line__variant' }, variant),
          h('div', { class: 'ak-line__qty' },
            h('button', { type: 'button', 'aria-label': `Remove one ${item.product_title}`, onclick: () => update(row, item.key, item.quantity - 1) }, '−'),
            h('span', { 'aria-label': 'Quantity' }, String(item.quantity)),
            h('button', { type: 'button', 'aria-label': `Add one ${item.product_title}`, onclick: () => update(row, item.key, item.quantity + 1) }, '+'),
          ),
          h('button', { type: 'button', class: 'ak-line__remove', onclick: () => update(row, item.key, 0) }, 'Remove'),
        ),
        h('span', { class: 'ak-line__price' }, money(item.final_line_price)),
      );
      list.append(row);
      if (img) stageImage(img);
    });
    $('[data-ak-cart-subtotal]', drawer).textContent = money(cart.total_price);
  }

  async function update(row, key, quantity) {
    row.classList.add('is-busy');
    try {
      renderCart(await cartApi.change(key, Math.max(0, quantity)));
    } catch (err) {
      row.classList.remove('is-busy');
      alert(err.message);
    }
  }

  function openCart() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
    document.documentElement.style.overflow = 'hidden';
    $('.ak-drawer__close', drawer).focus({ preventScroll: true });
  }

  function closeCart() {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    const panel = $('.ak-drawer__panel', drawer);
    const done = () => { drawer.hidden = true; };
    reduceMotion ? done() : panel.addEventListener('transitionend', done, { once: true });
    if (lastFocus) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', async (e) => {
    const opener = e.target.closest('[data-ak-cart-open]');
    if (opener && drawer) {
      e.preventDefault();
      openCart();
      renderCart(await cartApi.get());
    }
    if (e.target.closest('[data-ak-cart-close]')) closeCart();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCart();
      closeMenu();
    }
    // Keep focus inside the open drawer.
    if (e.key === 'Tab' && drawer && !drawer.hidden) {
      const focusables = $$('a[href], button:not([disabled]), input, select', drawer).filter((el) => el.offsetParent);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- Add to bag (any form with data-ak-add) ---------- */

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ak-add]');
    if (!form || !drawer) return;
    e.preventDefault();
    const button = $('[data-ak-submit]', form);
    const error = $('[data-ak-error]', form.closest('[data-ak-buy]') || form);
    if (button.disabled) return;
    button.classList.add('is-loading');
    if (error) error.hidden = true;
    try {
      await cartApi.add(new FormData(form));
      const cart = await cartApi.get();
      setCount(cart.item_count, true);
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1600);
      openCart();
      renderCart(cart);
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      } else {
        alert(err.message);
      }
    } finally {
      button.classList.remove('is-loading');
    }
  });

  /* ---------- Variant picker ---------- */

  function initBuy(buy) {
    const json = $('[data-ak-variants]', buy);
    if (!json || buy.dataset.akReady) return;
    buy.dataset.akReady = '1';
    const variants = JSON.parse(json.textContent);
    const scope = buy.closest('[data-ak-product]') || buy;
    const idInput = $('[data-ak-variant-id]', buy);
    const submit = $('[data-ak-submit]', buy);
    const label = $('[data-ak-submit-label]', buy);
    const fieldsets = $$('[data-ak-option]', buy);
    if (!fieldsets.length) return;

    const selected = () => fieldsets.map((fs) => ($('input:checked', fs) || {}).value);

    function refresh() {
      const values = selected();
      const variant = variants.find((v) => v.options.every((opt, i) => opt === values[i]));
      fieldsets.forEach((fs, i) => {
        const shown = $('[data-ak-option-value]', fs);
        if (shown) shown.textContent = values[i] || '';
        // Strike through values that have no available variant alongside the other current choices.
        $$('.ak-pill', fs).forEach((pill) => {
          const value = $('input', pill).value;
          const ok = variants.some((v) => v.available && v.options[i] === value && v.options.every((o, j) => j === i || o === values[j]));
          pill.classList.toggle('is-unavailable', !ok);
        });
      });

      if (!variant) {
        submit.disabled = true;
        label.textContent = 'Unavailable';
        return;
      }
      idInput.value = variant.id;
      submit.disabled = !variant.available;
      label.textContent = variant.available ? 'Add to bag' : 'Sold out';
      $$('[data-ak-price]', scope).forEach((el) => { el.textContent = money(variant.price); });
      if (scope.classList.contains('ak-pdp')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    fieldsets.forEach((fs) => fs.addEventListener('change', refresh));
    refresh();
  }

  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-ak-qty-step]');
    if (!step) return;
    const input = $('input', step.closest('[data-ak-qty]'));
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(step.dataset.akQtyStep));
  });

  /* ---------- Header + mobile menu ---------- */

  const header = $('[data-ak-header]');
  const menu = $('[data-ak-menu]');
  const burger = $('[data-ak-menu-toggle]');

  function closeMenu() {
    if (!menu || menu.hidden) return;
    menu.hidden = true;
    burger.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    header.classList.remove('is-menu');
  }

  if (burger) {
    burger.addEventListener('click', () => {
      const open = menu.hidden;
      if (!open) return closeMenu();
      menu.hidden = false;
      burger.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      header.classList.add('is-scrolled', 'is-menu');
    });
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) closeMenu(); });
  }

  /* ---------- Scroll-driven motion ---------- */

  const hero = $('[data-ak-hero]');
  const frames = $$('[data-ak-parallax]');
  const manifestos = $$('[data-ak-manifesto]');
  let lastY = window.scrollY;
  let ticking = false;

  function splitWords(root) {
    const p = $('[data-ak-words]', root);
    if (!p || p.dataset.split) return;
    p.dataset.split = '1';
    const words = p.textContent.trim().split(/\s+/);
    p.textContent = '';
    words.forEach((word, i) => {
      p.append(h('span', { class: 'w' }, word));
      if (i < words.length - 1) p.append(' ');
    });
  }
  manifestos.forEach(splitWords);

  function onScroll() {
    const y = window.scrollY;
    const vh = window.innerHeight;

    if (header && !header.classList.contains('is-menu')) {
      header.classList.toggle('is-scrolled', y > 24);
      header.classList.toggle('is-hidden', y > lastY && y > vh * 0.9 && (!drawer || drawer.hidden));
    }
    lastY = y;

    if (reduceMotion) return;

    if (hero) {
      const p = Math.min(1, Math.max(0, y / (hero.offsetHeight * 0.9)));
      hero.style.setProperty('--p', p.toFixed(4));
    }

    frames.forEach((frame) => {
      const r = frame.parentElement.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      const t = (r.top + r.height / 2 - vh / 2) / vh; // -1 … 1 through the viewport
      frame.style.setProperty('--py', `${(t * -5).toFixed(2)}%`);
    });

    manifestos.forEach((m) => {
      const words = $$('.w', m);
      const r = $('[data-ak-words]', m).getBoundingClientRect();
      // Light words from when the paragraph's top reaches 80% of the viewport until its bottom reaches 45%.
      const start = vh * 0.8;
      const end = vh * 0.45;
      const progress = Math.min(1, Math.max(0, (start - r.top) / (start - end + r.height)));
      const lit = Math.round(progress * words.length);
      words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
    });
  }

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { onScroll(); ticking = false; });
  }, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* ---------- Lion tilts toward the cursor ---------- */

  const tilt = $('[data-ak-tilt]');
  if (tilt && hero && finePointer && !reduceMotion) {
    let tx = 0, ty = 0, x = 0, y = 0, raf = 0;
    const loop = () => {
      x += (tx - x) * 0.06;
      y += (ty - y) * 0.06;
      tilt.style.setProperty('--ry', `${(x * 8).toFixed(2)}deg`);
      tilt.style.setProperty('--rx', `${(y * -6).toFixed(2)}deg`);
      raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.001 ? requestAnimationFrame(loop) : 0;
    };
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width - 0.5;
      ty = (e.clientY - r.top) / r.height - 0.5;
      if (!raf) raf = requestAnimationFrame(loop);
    });
  }

  /* ---------- Reveal on scroll ---------- */

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;

  /* ---------- Studio stage: lift product photos off their white backgrounds ---------- */

  // Product shots are mockups on white. In the browser we flood-fill the white backdrop away from
  // the edges inward (so white prints on the garment survive), and CSS lights it on a dark stage.
  // Photos that aren't on white (lifestyle shots, already-staged images) are left untouched.
  const STAGE_IMG = ':is(.ak-card__media, .ak-pdp__shot, .ak-drop__frame, .ak-line__img) img';
  const cutouts = new Map(); // source URL -> Promise<blob URL | null>

  function knockOut(image) {
    const scale = Math.min(1, 1400 / Math.max(image.naturalWidth, image.naturalHeight));
    const w = Math.round(image.naturalWidth * scale);
    const h = Math.round(image.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, w, h);
    const frame = ctx.getImageData(0, 0, w, h);
    const d = frame.data;
    const n = w * h;
    const bg = new Uint8Array(n);
    const stack = new Int32Array(n);
    let top = 0;
    const seed = (p) => {
      if (bg[p]) return;
      const i = p * 4;
      const lo = Math.min(d[i], d[i + 1], d[i + 2]);
      const hi = Math.max(d[i], d[i + 1], d[i + 2]);
      if (lo > 218 && hi - lo < 24) { bg[p] = 1; stack[top++] = p; }
    };
    for (let x = 0; x < w; x++) { seed(x); seed(n - w + x); }
    for (let y = 0; y < h; y++) { seed(y * w); seed(y * w + w - 1); }
    while (top) {
      const p = stack[--top];
      const x = p % w;
      if (x > 0) seed(p - 1);
      if (x < w - 1) seed(p + 1);
      if (p >= w) seed(p - w);
      if (p < n - w) seed(p + w);
    }
    let count = 0;
    for (let p = 0; p < n; p++) count += bg[p];
    if (count < n * 0.15) return null;

    // The garment's outermost pixel ring is blended with white: drop it, and soften the next ring.
    const ring = () => {
      const out = new Uint8Array(n);
      for (let p = 0; p < n; p++) {
        if (bg[p]) continue;
        const x = p % w;
        if ((x > 0 && bg[p - 1]) || (x < w - 1 && bg[p + 1]) || (p >= w && bg[p - w]) || (p < n - w && bg[p + w])) out[p] = 1;
      }
      return out;
    };
    const outer = ring();
    for (let p = 0; p < n; p++) if (outer[p]) bg[p] = 1;
    const soft = ring();
    for (let p = 0; p < n; p++) {
      if (bg[p]) d[p * 4 + 3] = 0;
      else if (soft[p]) d[p * 4 + 3] = 150;
    }
    ctx.putImageData(frame, 0, 0);
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b ? URL.createObjectURL(b) : null), 'image/png'));
  }

  function cutoutUrl(src) {
    if (!cutouts.has(src)) {
      cutouts.set(src, new Promise((resolve) => {
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.onload = () => {
          try { resolve(knockOut(probe)); } catch (e) { resolve(null); } // tainted canvas etc.: keep the original
        };
        probe.onerror = () => resolve(null);
        probe.src = src;
      }));
    }
    return cutouts.get(src);
  }

  function stageImage(img) {
    if (img.dataset.akStaged) return;
    img.dataset.akStaged = '1';
    const reveal = () => img.classList.add('is-ready');
    const run = () => {
      const src = img.currentSrc || img.src;
      if (!src || src.startsWith('blob:')) return reveal();
      setTimeout(reveal, 3000); // never leave a product hidden if processing stalls
      cutoutUrl(src).then((url) => {
        if (url) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = url;
          img.classList.add('is-cut');
        }
        reveal();
      });
    };
    if (img.complete && img.naturalWidth) run();
    else {
      img.addEventListener('load', run, { once: true });
      img.addEventListener('error', reveal, { once: true });
    }
  }

  function init(root = document) {
    $$(STAGE_IMG, root).forEach(stageImage);
    $$('[data-reveal]', root).forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
    $$('[data-ak-buy]', root).forEach(initBuy);
    $$('[data-ak-manifesto]', root).forEach(splitWords);
  }
  init();
  onScroll();

  /* ---------- Product gallery dots (mobile swipe) ---------- */

  $$('[data-ak-gallery]').forEach((gallery) => {
    const dots = $$('.ak-pdp__dots span', gallery.parentElement);
    if (!dots.length) return;
    gallery.addEventListener('scroll', () => {
      const i = Math.round(gallery.scrollLeft / gallery.clientWidth);
      dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
    }, { passive: true });
  });

  /* ---------- Full-screen image zoom (product page) ---------- */

  $$('[data-ak-zoom-dialog]').forEach((dialog) => {
    const section = dialog.closest('[data-ak-product]') || document;
    const triggers = $$('[data-ak-zoom]', section);
    const stage = $('[data-ak-zoom-stage]', dialog);
    const img = $('[data-ak-zoom-img]', dialog);
    const count = $('[data-ak-zoom-count]', dialog);
    let index = 0;
    let last = null;

    // Fine pointers pan the oversized image with the cursor; touch scrolls it natively.
    const pan = (e) => {
      if (!finePointer || !e) return;
      last = e;
      const r = stage.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
      const y = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
      const dx = Math.max(0, img.offsetWidth - r.width);
      const dy = Math.max(0, img.offsetHeight - r.height);
      img.style.transform = `translate3d(${-dx * x}px, ${-dy * y}px, 0)`;
    };

    const show = (i) => {
      index = (i + triggers.length) % triggers.length;
      img.classList.remove('is-loaded');
      img.onload = () => {
        img.classList.add('is-loaded');
        if (finePointer) {
          pan(last);
        } else {
          stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) / 2;
          stage.scrollTop = (stage.scrollHeight - stage.clientHeight) / 2;
        }
      };
      const src = triggers[index].dataset.akZoom;
      const want = index;
      img.classList.remove('is-cut');
      cutoutUrl(src).then((url) => {
        if (want !== index) return; // user moved on before this one finished
        img.classList.toggle('is-cut', Boolean(url));
        img.src = url || src;
      });
      img.alt = $('img', triggers[index])?.alt || '';
      if (count) count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(triggers.length).padStart(2, '0')}`;
    };

    triggers.forEach((btn, i) => btn.addEventListener('click', (e) => {
      if (typeof dialog.showModal !== 'function') return;
      last = e;
      dialog.showModal();
      document.documentElement.classList.add('is-zoom');
      show(i);
    }));

    stage.addEventListener('pointermove', pan);
    stage.addEventListener('click', () => { if (finePointer) dialog.close(); });
    $$('[data-ak-zoom-step]', dialog).forEach((b) => b.addEventListener('click', () => show(index + Number(b.dataset.akZoomStep))));
    $('[data-ak-zoom-close]', dialog).addEventListener('click', () => dialog.close());
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') show(index + 1);
      if (e.key === 'ArrowLeft') show(index - 1);
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('is-zoom');
      img.removeAttribute('src');
      img.style.transform = '';
      triggers[index].focus({ preventScroll: true });
    });
  });

  /* ---------- Collection sort ---------- */

  $$('[data-ak-sort]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });

  /* ---------- Soft page transitions between internal pages ---------- */

  if (!reduceMotion) {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      if (a.target === '_blank' || a.hasAttribute('download') || a.dataset.akCartOpen !== undefined) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.protocol.indexOf('http') !== 0) return;
      if (url.pathname === window.location.pathname && url.hash) return;
      if (/\/(checkout|cart|account)/.test(url.pathname)) return;
      e.preventDefault();
      document.documentElement.classList.add('is-leaving');
      setTimeout(() => { window.location.href = url.href; }, 320);
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) document.documentElement.classList.remove('is-leaving');
    });
  }

  /* ---------- Theme editor support ---------- */

  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
